import { Component, type ErrorInfo, type ReactNode } from "react"
import { Button } from "@/components/ui/button"
import { BiscuitMini } from "@/components/biscuit/BiscuitMini"

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary] Render error:", error, info.componentStack)
  }

  private handleReload = () => {
    this.setState({ error: null })
    window.location.reload()
  }

  private handleReset = () => {
    // A deterministic render error would re-throw if we only cleared state and
    // re-rendered the same subtree. Navigate to a safe route with a full load so
    // the broken tree is actually unmounted.
    this.setState({ error: null })
    window.location.assign("/home")
  }

  render() {
    if (!this.state.error) return this.props.children

    return (
      <div
        role="alert"
        aria-live="assertive"
        className="min-h-dvh flex items-center justify-center bg-background p-4"
      >
        <div className="max-w-md w-full bg-card rounded-2xl border p-6 text-center space-y-4 shadow-sm">
          <div className="flex justify-center" aria-hidden="true">
            <BiscuitMini mood="worried" />
          </div>
          <div className="space-y-1">
            <h1 className="text-xl font-semibold">Biscuit tripped <span aria-hidden="true">🐾</span></h1>
            <p className="text-sm text-muted-foreground">
              Something broke. Try going back, or reload to get Biscuit back on his paws.
            </p>
          </div>
          {import.meta.env.DEV && (
            <pre className="text-left text-xs bg-muted rounded-xl p-3 overflow-auto max-h-40 whitespace-pre-wrap" aria-label="Error details">
              {this.state.error.message}
            </pre>
          )}
          <div className="flex gap-2 justify-center">
            <Button
              type="button"
              variant="outline"
              onClick={this.handleReset}
              className="rounded-xl min-h-11 motion-reduce:transition-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              Go back
            </Button>
            <Button
              type="button"
              onClick={this.handleReload}
              className="rounded-xl min-h-11 motion-reduce:transition-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              Reload
            </Button>
          </div>
        </div>
      </div>
    )
  }
}
